/**
 * Clone and clone-package calls (specs/clone-packages.md). Errors carry the server's
 * message and code (name_taken, schema_mismatch, …).
 */
import API_ENDPOINTS from '../../config/api';
import { putToPresignedUrl } from '../../utils/storageUpload';

async function request(url, options = {}) {
  const res = await fetch(url, { credentials: 'include', ...options });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.code = body.code;
    err.details = body.details;
    throw err;
  }
  return body;
}

const post = (url, payload) => request(url, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload || {}),
});

export const nameAvailable = (kind, name) => request(API_ENDPOINTS.ADMIN_CLONE_NAME_AVAILABLE(kind, name));

export function cloneOnServer(kind, sourceId, { name, mode, includeFiles }) {
  return kind === 'dataset'
    ? post(API_ENDPOINTS.ADMIN_DATASET_CLONE(sourceId), { name, includeFiles })
    : post(API_ENDPOINTS.ADMIN_INSTANCE_CLONE(sourceId), { name, mode });
}

export async function downloadPackage(kind, sourceId, { mode, includeFiles }) {
  const url = kind === 'dataset'
    ? API_ENDPOINTS.ADMIN_DATASET_CLONE_PACKAGE(sourceId, includeFiles)
    : API_ENDPOINTS.ADMIN_INSTANCE_CLONE_PACKAGE(sourceId, mode);
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const name = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || `kuutio-${kind}-${sourceId}.zip`;
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  a.click();
  URL.revokeObjectURL(href);
}

export async function uploadPackage(kind, file, { onProgress } = {}) {
  // Packages run to gigabytes, past the Vercel middleware's ~4.5 MB body cap, so
  // the file goes straight to object storage with a presigned PUT (#973).
  const { key, url, headers } = await post(API_ENDPOINTS.ADMIN_CLONE_PACKAGE_UPLOAD_URL(), { name: file.name, size: file.size });
  await putToPresignedUrl(url, headers, file, (loaded) => onProgress?.({ loaded, total: file.size }));
  return post(API_ENDPOINTS.ADMIN_CLONE_PACKAGES(), { kind, key });
}

export const importUploadedPackage = (uploadId, name) => post(API_ENDPOINTS.ADMIN_CLONE_PACKAGE_IMPORT(uploadId), { name });
